import { useMemo } from 'react'

import { matchFilterKey, matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { EntityOptions } from '@redux/modules/module'

import type { AttachmentModel } from '@models/types'

const watchEntityKeys = ['attachments']

type AttachmentFilters = {
  attachable_id?: number,
  attachable_type?: string,
  attachment_type?: string,
}

type UseAttachmentsOptions = {
  filters?: AttachmentFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useAttachments(options: UseAttachmentsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    attachable_id: filterAttachableId,
    attachable_type: filterAttachableType,
    attachment_type: filterAttachmentType,
  } = filters

  const {
    updatedEntities: { attachments: attachmentsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { attachments } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredAttachments = useMemo(() => {
    const filtered = Object.values(attachments).filter((attachment: AttachmentModel) => {
      const { attachable_id, attachable_type, attachment_type } = attachment

      const matchAttachableId = matchFilterKey(attachable_id, filterAttachableId)
      const matchAttachableType = matchFilterKey(attachable_type, filterAttachableType)
      const matchAttachmentType = matchFilterKey(attachment_type, filterAttachmentType)

      return matchAttachableType && matchAttachableId && matchAttachmentType
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [attachmentsUpdatedAt, JSON.stringify(filters)])

  const filteredAttachmentsCount = filteredAttachments.length
  const hasFilteredAttachments = !!filteredAttachmentsCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAttachmentsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const { loading: loadingAttachments } = useReduxAction(
    'attachments',
    'loadAttachments',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: () => performHttpRequests,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAttachments,
    filteredAttachmentsCount,
    hasFilteredAttachments,
    loading: loadingAttachments,
  }
}

export default useAttachments
